import { Button } from 'Components/Core';
import PopupSection from 'Components/Layout/Popup';
import { hasValue } from 'Lib/Any';
import { useCallback, useEffect, useState } from 'react';
import { ColorResult, RGBColor, SketchPicker } from 'react-color';
import { withInjectedProps } from '../InjectedPropsContext';
import { ChangeEventWithData } from '../types';
import './ColorPickerInput.scss';

type ColorValue = {
  Red: number;
  Green: number;
  Blue: number;
};

type ColorPickerInputProps = {
  name?: string;
  onChange?: (event: ChangeEventWithData<HTMLInputElement, ColorValue>) => void;
  value?: ColorValue | undefined;
};

const valueToColor = (value: ColorValue | undefined): RGBColor | undefined =>
  hasValue(value) && hasValue(value!.Red) && hasValue(value!.Green) && hasValue(value!.Blue)
    ? { r: value!.Red, g: value!.Green, b: value!.Blue }
    : undefined;

const colorToValue = (color: RGBColor): ColorValue => ({
  Red: color.r,
  Green: color.g,
  Blue: color.b,
});

const colorToCss = (color: RGBColor): string => `rgba(${color.r}, ${color.g}, ${color.b}, ${color.a ?? 1})`;
const nullColorCss = {
  background: `linear-gradient(to top right, #fff calc(50% - 1px), red , #fff calc(50% + 1px) )`,
};

const ColorPickerInputBase: React.FC<ColorPickerInputProps> = ({ value, onChange }) => {
  const [internalValue, setInternalValue] = useState(value);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  const handleChange = useCallback(
    (result: ColorResult, e: React.ChangeEvent<HTMLInputElement>) => {
      (e as ChangeEventWithData<HTMLInputElement, ColorValue>).data = {
        value: colorToValue(result.rgb),
        submitValues: true,
      };
      onChange?.(e);
    },
    [onChange]
  );

  const handleOpen = () => {
    setIsOpen(true);
  };
  const handleClose = () => {
    setIsOpen(false);
  };
  const resetAction = (e: any) => {
    setIsOpen(false);
    e.data = { value: null, submitValues: true };
    onChange?.(e);
  };

  const selectedColor = valueToColor(internalValue);

  return (
    <>
      <PopupSection
        open={isOpen}
        onOpen={handleOpen}
        onClose={handleClose}
        trigger={
          <div className="ColorPickerInput-Button">
            <div
              className="ColorPickerInput-ColorSwatch"
              style={selectedColor ? { background: colorToCss(selectedColor) } : nullColorCss}
            />
          </div>
        }
        position="right center"
        content={
          <>
            <SketchPicker
              color={selectedColor}
              onChange={(colorResult, _) => setInternalValue(colorToValue(colorResult.rgb))}
              onChangeComplete={(colorResult, e) => handleChange(colorResult, e)}
              disableAlpha={true}
              presetColors={[
                '#d5001c',
                '#ff3029',
                '#fef9c3',
                '#FFECD9',
                '#ca8a04',
                '#c0ebc9',
                '#32c864',
                '#bbdefe',
                '#1676da',
                '#000000',
                '#404040',
                '#DCDCDC',
                '#F3F3F3',
                '#FFFFFF',
              ]}
            />
            <Button light onClick={(e) => resetAction(e)} className="ColorPickerInput-ClearButton">
              Clear Color
            </Button>
          </>
        }
        style={{ padding: 0 }}
      />
    </>
  );
};

const ColorPickerInput = withInjectedProps(ColorPickerInputBase);

export { ColorPickerInput };
export default ColorPickerInput;
